import {
  CreditHistory,
  FFHouseholdDTO,
  LoanPurpose,
  XSellKind
} from '@/models/simulation/Simulation';
import {MarketType} from '@/models/simulation/MarketType';
import {LoanPurposeActivity} from '@/models/simulation/LoanPurposeActivity';
import {LoanPurposeWhat} from '@/models/simulation/LoanPurposeWhat';
import {LoanPurposeHow} from '@/models/simulation/LoanPurposeHow';
import {BankSymbol} from '@/commons/dictionaries/BankSymbol';
import {Utils} from '@/services/utils/BasicUtils';
import {OfferDataTableElementKey} from '@/components/calculator/results/offers/OfferTableElement';
import {OfferFilter} from '@/components/calculator/results/OfferSorting';
import {Currency} from '@/modules/multiForm/shared/MultiformDictionaries';
import EnvironmentService from '@/env/EnvironmentService';
import {FirstOrSubsequentRealEstate} from '@/models/simulation/FirstOrSubsequentRealEstate';

export class SimulationDemandDTO {
  loanAmount: Nullable<number> = null;
  loanPeriodInMonths: Nullable<number> = null;
  loanPeriod: Nullable<number> = null;
  hypothecValue: Nullable<number> = null;
  fixedInstallments: boolean = true;
  constructionPeriod: Nullable<number> = null;
  currency: Currency = EnvironmentService.Environment.currentCurrency();
  purposeNotAsCollateral: boolean = false;
  additionalRequests: AdditionalRequests = {
    creditAllInitialCosts: false,
    requiredInsurances: {
      realEstateInsurance: EnvironmentService.Environment.getAppDomainConfig().simulationFilters.realEstateInsurance,
    },
    referenceRate: null,
    referenceRateWibor3m: null,
    referenceRateWibor6m: null,
    referenceRateWiron1m: null,
    creditCardDemands: [],
    replacements: new Replacements(),
    acquireCreditCard: false,
  };

  creditHistory: CreditHistory = {exists: false, };
  loanPurpose: LoanPurpose = {
    activity: LoanPurposeActivity.BUYING,
    what: LoanPurposeWhat.APARTMENT,
    market: MarketType.PRIMARY_MARKET,
    how: LoanPurposeHow.ON_YOUR_OWN,
  };

  firstOrSubsequentRealEstate: Nullable<FirstOrSubsequentRealEstate> =
    EnvironmentService.Environment.getAppDomainConfig().calculator.hasFirstOrSubsequentRealEstateInfo
      ? FirstOrSubsequentRealEstate.FIRST : null;

  households?: FFHouseholdDTO[] = [];
  acceptableDtiPercentage: Nullable<number> = null;
  organizationDomain: string = 'lendi.pl';
}

export const MORTGAGE_DEFAULT_LOAN_PERIOD_IN_YEARS = 30;
export const MORTGAGE_DEFAULT_LOAN_PERIOD_FOR_PURPOSE_LOAN_IN_YEARS = 20;

interface RefusedOffersUserInput {
  selectedBanks: BankSymbol[];
}

export class SimulationDemand extends SimulationDemandDTO {
  selectedBanks: BankSymbol[] = [];
  selectedOffers: string[] = [];
  pinnedOffers: string[] = [];
  collateral: LoanPurposeWhat = LoanPurposeWhat.APARTMENT;
  hideOfferName: boolean = false;
  showOnlySelected: boolean = false;
  isAscending: boolean = true;
  sortBy: OfferDataTableElementKey = OfferDataTableElementKey.TOTAL_SUM;
  offerFilters: Partial<OfferFilter> = {};
  refusedOffers: RefusedOffersUserInput = {
    selectedBanks: [],
  };

  homeStartProgramInput?: {
    numberOfAdult: Nullable<number>,
    numberOfChildren?: Nullable<number>,
    city: Nullable<string>
    propertyArea: Nullable<number>
    incomeSum: Nullable<number>
    isBuildingHouse: boolean
  } = {
    numberOfAdult: 1,
    numberOfChildren: 0,
    city: null,
    propertyArea: 50,
    incomeSum: 7000,
    isBuildingHouse: this.loanPurpose.what === LoanPurposeWhat.HOUSE,
  }

  acceptableDtiPercentage: Nullable<number> = null;

  constructor(dto?: Partial<SimulationDemandDTO>) {
    super();
    if (dto) {
      Utils.assign(this, dto);
    }
  }
}

export class MortgageSimulationDemand extends SimulationDemand {
  hypothecValue: number = 500_000;
  explicitBridgingPeriod: number = 0;

  isLoanPeriodInMonths: boolean = true;
  loanPeriodInMonths: number = MORTGAGE_DEFAULT_LOAN_PERIOD_IN_YEARS * 12;
  offerFilters: Partial<OfferFilter> = {
    notEKOTrue: true,
    xSellTrue: true,
  };

  acceptableDtiPercentage: Nullable<number> = 40;

  constructor(dto?: Partial<MortgageSimulationDemand>) {
    super(dto);
    if (dto) {
      Utils.assign(this, dto);
    }
  }
}

export interface RequiredInsurances {
  lifeInsurance?: boolean;
  realEstateInsurance: boolean;
}

export interface IReplacment {
  loanId: string;
}

export interface LoanPeriodReplacement extends IReplacment{
  newLoanPeriodInMonths: number;
}

export interface MarginReplacement extends IReplacment{
  newInitialValue?: number;
  newEventualValue?: number;
}

export interface DefaultReplacement extends IReplacment{
  newValue: number;
}

export interface InsuranceReplacement {
  loanId?: string;
  insuranceVariantId: string;
  insuranceKind: XSellKind;
  insuranceId: string;
}

export type Replacement = LoanPeriodReplacement | MarginReplacement | DefaultReplacement | InsuranceReplacement;

export interface InsuranceDemands {
  insuranceId: string;
  kind: XSellKind;
  loanId: string;
  required: boolean;
}

export enum ReplacementKind {
  MARGIN = 'marginReplacements',
  PROVISION = 'provisionReplacements',
  LOAN_AMOUNT = 'loanAmountReplacements',
  LOAN_PERIOD = 'loanPeriodReplacements',
  BROKER_COMMISSION = 'brokerCommissionReplacements',
  INSURANCE = 'insuranceVariantReplacements',
}

// Infers the appropriate replacement type based on the provided ReplacementKind(enum).
// Returns a specific type based on the kind, such as DefaultReplacement, InsuranceReplacement, etc.
export type InferredReplacement<T extends ReplacementKind> = T extends infer R
  ? R extends (ReplacementKind.BROKER_COMMISSION | ReplacementKind.LOAN_AMOUNT | ReplacementKind.PROVISION) ? DefaultReplacement :
  R extends ReplacementKind.INSURANCE ? InsuranceReplacement :
  R extends ReplacementKind.LOAN_PERIOD ? LoanPeriodReplacement :
  R extends ReplacementKind.MARGIN ? MarginReplacement : never : never

export class Replacements {
  [ReplacementKind.MARGIN]: MarginReplacement[] = [];
  [ReplacementKind.PROVISION]: DefaultReplacement[] = [];
  [ReplacementKind.LOAN_AMOUNT]: DefaultReplacement[] = [];
  [ReplacementKind.LOAN_PERIOD]: LoanPeriodReplacement[] = [];
  [ReplacementKind.BROKER_COMMISSION]: DefaultReplacement[] = [];
  [ReplacementKind.INSURANCE]: InsuranceReplacement[] = [];
}

export interface LoanVariant {
  originLoanId: string;
  variantLoanId: string;
}

export interface CreditCardDemand {
  acquired: boolean;
  loanId: string;
}

export interface ReferenceRate {
  referenceRate: Nullable<number>;
  referenceRateWibor3m: Nullable<number>;
  referenceRateWibor6m: Nullable<number>;
  referenceRateWiron1m: Nullable<number>;
}

export interface AdditionalRequests extends ReferenceRate {
  creditAllInitialCosts: boolean;
  creditCardDemands: CreditCardDemand[];
  requiredInsurances: Nullable<RequiredInsurances>;
  replacements?: Replacements;
  insuranceDemands?: InsuranceDemands[];
  loanVariants?: LoanVariant[];
  acquireCreditCard: boolean;
}

export class CashSimulationDemand extends SimulationDemand {
  customerProfile: string = 'SUPER';
  commissionLevel: string = 'L0';
  brokerCommission: Nullable<number> = null;
  minProvision: Nullable<number> = null;
  loanTypes = {consolidation: true, cash: true, };
  bankRelations: [] = [];
  loanPeriod: number = 60;
  offerFilters: Partial<OfferFilter> = {
    notEKOTrue: true,
  };

  additionalRequests: AdditionalRequests = {
    creditAllInitialCosts: true,
    requiredInsurances: {
      realEstateInsurance: true,
    },
    referenceRate: null,
    referenceRateWibor3m: null,
    referenceRateWibor6m: null,
    referenceRateWiron1m: null,
    creditCardDemands: [],
    replacements: new Replacements(),
    acquireCreditCard: false,
  };

  constructor(dto?: Partial<CashSimulationDemand>) {
    super(dto);
    if (dto) {
      Utils.assign(this, dto);
    }
  }
}
